
import { Box, Divider, Stack, Typography } from "@mui/material";
import SectionTabs from "../SectionTabs";
import CompositionHeader from "../CompositionHeader";
import { PatientCard, PractitionerCard, OrganizationSection, HospitalBanner } from "../InfoCards";
import EncounterSection from "../EncounterSection";
import DocumentReferenceSection from "../DocumentReferenceSection";
import { labelSx, valueSx, pillSx, formatDate } from "../styles";

const PrescriptionView = ({ bundle, setShowPdfPreview }: { bundle: any; setShowPdfPreview: (v: boolean) => void }) => {
    const meds = bundle?.medicationRequest || [];
    const conditions = bundle?.conditions || [];
    const composition = bundle?.composition || {};
    const patient = bundle || {};

    const sections = [
        {
            id: "medications",
            label: "Medications",
            content:
                meds.length === 0 ? (
                    <Typography sx={labelSx}>No medications found.</Typography>
                ) : (
                    <Stack spacing={2}>
                        {meds.map((med: any, idx: number) => (
                            <Box key={`${med.medicationName || "med"}-${idx}`} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                    <Typography sx={{ ...valueSx, fontSize: 16 }}>{med.medicationName || "Medication"}</Typography>
                                    <Box sx={pillSx}>{med.medicationStatus || ""}</Box>
                                </Stack>
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Dosage</Typography>
                                        <Typography sx={valueSx}>{med.dosageInstruction || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Route</Typography>
                                        <Typography sx={valueSx}>{med.dosageRoute || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Frequency</Typography>
                                        <Typography sx={valueSx}>{`${med.dosageFrequency || "-"} x ${med.dosagePeriod || "-"} ${med.dosagePeriodUnit || ""}`}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Prescribed On</Typography>
                                        <Typography sx={valueSx}>{formatDate(med.datePrescribed)}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column", gridColumn: { xs: "1", sm: "1 / 2", lg: "1 / 2" } }}>
                                        <Typography sx={labelSx}>Requester</Typography>
                                        <Typography sx={valueSx}>{med.requester || "—"}</Typography>
                                    </Box>
                                </Box>
                                {med.dosageAdditionalInstructions && med.dosageAdditionalInstructions.length > 0 && (
                                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                                        <Typography sx={labelSx}>Additional Instructions</Typography>
                                        <Typography sx={valueSx}>{med.dosageAdditionalInstructions.join(", ")}</Typography>
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Stack>
                )
        },
        {
            id: "conditions",
            label: "Conditions",
            content:
                conditions.length === 0 ? (
                    <Typography sx={labelSx}>No conditions captured.</Typography>
                ) : (
                    <Stack spacing={1.5}>
                        {conditions.map((cond: any, idx: number) => (
                            <Box key={`condition-${idx}`} sx={{ p: 1.5, border: "1px dashed", borderColor: "divider", borderRadius: 1 }}>
                                <Typography sx={valueSx}>{cond.conditionText || "Condition"}</Typography>
                                <Box
                                    sx={{
                                        mt: 0.5,
                                        display: "grid",
                                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
                                        gap: 2
                                    }}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Type</Typography>
                                        <Typography sx={valueSx}>{cond.conditionType || "—"}</Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Typography sx={labelSx}>Recorded</Typography>
                                        <Typography sx={valueSx}>{formatDate(cond.recordedDate)}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                )
        },
        {
            id: "attachments",
            label: "Attachments",
            content: bundle?.documentReference || bundle?.binary?.data ? (
                <DocumentReferenceSection
                    documentReference={bundle?.documentReference}
                    binary={bundle?.binary}
                    onPreviewBase64={() => setShowPdfPreview(true)}
                />
            ) : (
                <Typography sx={labelSx}>No attachments available.</Typography>
            )
        }
    ];

    return (
        <Stack spacing={3}>
            <HospitalBanner organization={bundle?.organization} />
            <CompositionHeader composition={composition} />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: "8px !important" }}>
                <PatientCard patient={patient} />
                <PractitionerCard practitioner={patient} />
            </Stack>

            <OrganizationSection organization={bundle?.organization} />
            <EncounterSection encounter={bundle?.encounter} />

            <Divider />
            <SectionTabs sections={sections} initialId="medications" />
        </Stack>
    );
};

export default PrescriptionView;
